import { useT } from '../../i18n';
import { L } from '../tokens';
import { useViewportKind } from '../useViewport';
import { BarChart, BigFigure, Card, Message, Notes, PeriodPills, ReportPage, StatRow } from './ReportParts';
import { periodBody } from './reportsApi';
import { bigMoney, countText, moneyText } from './reportFormat';
import { useReportPeriod } from './periodStore';
import { useReport } from './useReport';

const KINDS = ['this_week', 'this_month', 'this_year', 'custom'];

// The Needs attention tab each row opens (the tab is kept in the router's memory, never in the address)
const TAB = { price_above_order: 'price', charge_not_ordered: 'price', quantity_differs: 'quantity', duplicate_invoice: 'duplicate' };
const casesLink = (row) => ({ pathname: '/cases/attention', state: { tab: TAB[row] } });

// Leo's "Report: discrepancies" (updated board): the errors found in the period with the chart, then two cards: Invoice (Price above order, Quantity differs, Charge not ordered, Missing line
// item, Duplicate invoice) and Order and confirmation (Confirmation differs from the order), with Documents late and Price increases apart ("not in total"). The names are the board's, mapped onto
// today's stored problem types. A row nothing detects yet (Missing line item, Confirmation differs) says "not checked yet" and is never shown as 0. Duplicate invoice keeps its count and
// amount but stays outside the total (LEO-FEEDBACK G20).
export default function DiscrepanciesView() {
  const t = useT();
  const phone = useViewportKind() === 'phone';
  const [period, setPeriod] = useReportPeriod('past');
  const { status, data, retry } = useReport('discrepancies', periodBody(period));
  const casesText = (n) => (n === 1 ? t('reports.disc.cases.one') : t('reports.disc.cases', { n: countText(n) }));
  const errorRow = (key) => {
    const r = data.rows.find((x) => x.row === key);
    if (!r.checked) return <StatRow key={key} testid={`disc-row-${key}`} label={t(`reports.disc.row.${key}`)} count={t('reports.disc.notChecked')} amount="" />;
    return <StatRow key={key} to={casesLink(key)} testid={`disc-row-${key}`} label={t(`reports.disc.row.${key}`)} count={casesText(r.cases)} amount={r.cases_with_money > 0 ? moneyText(r.amount, data.currency) : ''} />;
  };
  const line = (key, label, value) => (
    <div key={key} data-testid={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 15, color: L.muted }}><span>{label}</span><span style={{ color: L.text, fontWeight: 600 }}>{value}</span></div>
  );
  return (
    <ReportPage title={t('reports.disc.title')} backTo="/reports" pills={<PeriodPills kinds={KINDS} value={period} onChange={setPeriod} />} testid="report-discrepancies">
      {status !== 'ready' ? <Message status={status} onRetry={retry} /> : (
        <>
          <Card testid="disc-top" style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : 'minmax(220px, 1fr) minmax(0, 2fr)', gap: 24, alignItems: 'center' }}>
            <BigFigure label={t('reports.disc.identified')} value={bigMoney(data.total.amount, data.currency)} color={L.accent} sub={casesText(data.total.cases)} />
            <BarChart series={data.series} grain={data.period.grain} currency={data.currency} />
          </Card>
          <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 18 }}>
            <Card title={t('reports.disc.group.invoice')} testid="disc-card-invoice">
              {['price_above_order', 'quantity_differs', 'charge_not_ordered', 'missing_line_item', 'duplicate_invoice'].map(errorRow)}
              {data.duplicates_outside_total && data.duplicates_outside_total.cases > 0 && <p data-testid="disc-duplicates-note" style={{ margin: '8px 0 0', fontSize: 13, color: L.muted }}>{t('reports.disc.note.duplicates')}</p>}
            </Card>
            <Card title={t('reports.disc.group.order')} testid="disc-card-order">
              {errorRow('confirmation_differs')}
              <div style={{ borderTop: `1px solid ${L.border}`, marginTop: 14, paddingTop: 6 }}>
                {line('disc-late', t('reports.who.late'), t('reports.who.posWithoutInvoice', { n: countText(data.documents_late.pos_without_invoice) }))}
                {line('disc-prices', t('reports.who.prices'), `${countText(data.price_increases.items)} · +${moneyText(data.price_increases.added_cost, data.currency)}`)}
              </div>
            </Card>
          </div>
          <Notes notes={{}} extra={[
            data.notes.cases_without_exact_date > 0 ? t('reports.disc.note.dates', { n: countText(data.notes.cases_without_exact_date) }) : null,
            data.notes.cases_without_amount > 0 ? t('reports.disc.note.noAmount', { n: countText(data.notes.cases_without_amount) }) : null,
            data.price_increases.over_cap > 0 ? t('reports.disc.priceIncreases.cap', { n: countText(data.price_increases.over_cap) }) : null,
          ]} />
        </>
      )}
    </ReportPage>
  );
}
